import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DragStart } from '@hello-pangea/dnd';
import './kanvan.style.css';
import { FaFire } from '@react-icons/all-files/fa/FaFire';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaSync } from '@react-icons/all-files/fa/FaSync';
import { FaInbox } from '@react-icons/all-files/fa/FaInbox';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaTag } from '@react-icons/all-files/fa/FaTag';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaColumns } from '@react-icons/all-files/fa/FaColumns';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FaPaperclip } from '@react-icons/all-files/fa/FaPaperclip';
import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import Breadcrumb from '../commons/Breadcrumb';
import PageHeader from '../commons/PageHeader';
import Confirm, { type ConfirmConfig, useConfirm } from '../commons/Confirm';
import DateFormat from '../commons/componentes/DateFormat';
import {
  ACTIVIDAD_VENCIDA_DATE_STYLE,
  EMPRESA_VENCIMIENTO_DATE_PATTERN,
} from '../shared/date/date-formats';
import ProfaseModal from '../commons/ProfaseModal';
import ActividadAdicionalModal from './ActividadAdicionalModal';
import KanbanForm from './KanbanForm';
import ActividadMailPanel from './ActividadMailPanel';
import ActividadAdjuntosListModal, {
  buildAdjuntosModalContextFromKanbanTask,
  type ActividadAdjuntosListModalContext,
} from './ActividadAdjuntosListModal';
import { getKanbanEmpresa } from './kanban-task-context';
import {
  actualizarActividadAdicional,
  actividadAdicionalEditDisabledReason,
  crearActividadAdicional,
  eliminarActividadAdicional,
  isActividadAdicional,
  puedeCrearActividadAdicional,
  puedeEditarActividadAdicional,
  resolveWorkflowInstanciaEstado,
  resolveWorkflowInstanciaId,
  type ActividadAdicionalFormPayload,
} from '../shared/workflow/actividad-adicional';
import { actividadMailStatusLabel } from '../shared/workflow/actividad-mail';
import ShieldIcon from '../commons/ShieldIcon';
import {
  playSound,
  playSoundDeferred,
  resolveActividadEstado,
  shouldPlayWrongSoundOnFinalizadoTransitionError,
} from '../shared/audio/audio';

const API_URL = '/api/usuario/me/actividad';
const PLAY_CARD_SOUND = '/assets/audio/play-card.mp3';
const MAGIC_SOUND = '/assets/audio/magic.mp3';
const WRONG_SOUND = '/assets/audio/wrong.mp3';

function playWrongSound(): void {
  playSound(WRONG_SOUND);
}

function playWrongSoundForTransitionError(fromColumnId: string, toColumnId: string): void {
  if (shouldPlayWrongSoundOnFinalizadoTransitionError(fromColumnId, toColumnId)) {
    playSoundDeferred(WRONG_SOUND);
  }
}

function playCardSound(): void {
  playSound(PLAY_CARD_SOUND);
}

/** Defer so drag/drop handlers don't block autoplay (same reason wrong works async). */
function playCardSoundDeferred(): void {
  window.setTimeout(() => playCardSound(), 0);
}

function beginCardMoveFeedback(fromColumnId: string, toColumnId: string): void {
  if (fromColumnId === toColumnId) return;
  playCardSoundDeferred();
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function mergePostitDragStyle(
  draggableStyle: React.CSSProperties | undefined,
  isDragging: boolean,
): React.CSSProperties {
  const base = { ...(draggableStyle ?? {}) };
  const boxShadow = isDragging
    ? '0 8px 24px rgba(0,0,0,0.18)'
    : '0 2px 6px rgba(0,0,0,0.08)';

  if (isDragging) {
    return { ...base, opacity: 0.85, boxShadow };
  }

  const transform = base.transform;
  const needsRotate =
    !transform
    || transform === 'none'
    || /^translate\(\s*0(px)?\s*,?\s*0(px)?\s*\)$/i.test(transform);

  return {
    ...base,
    opacity: 1,
    boxShadow,
    ...(needsRotate ? { transform: 'rotate(1deg)' } : {}),
  };
}

// ── Column definitions ────────────────────────────────────────────────────────

interface ColumnDef {
  id: string;
  title: string;
  color: string;
  estados: string[];
  taskColor: TaskColor;
}
type TaskColor = 'yellow' | 'red' | 'white' | 'gray' | 'blue' | 'green';

const COLUMNS: ColumnDef[] = [
  { id: 'BLOQUEADO', title: 'Bloqueado', color: '#64748b', estados: ['BLOQUEADO', 'BLOCKED'], taskColor: 'gray' },
  { id: 'NO_AUTORIZADO', title: 'No Autorizado', color: '#ef4444', estados: ['NO_AUTORIZADO'], taskColor: 'gray' },
  { id: 'PENDIENTE', title: 'Pendiente', color: '#f59e0b', estados: ['PENDIENTE', 'GENERADO', 'TODO', 'PENDING'], taskColor: 'yellow' },
  { id: 'PENDIENTE_DE_AUTORIZACION', title: 'Pendiente de Autorización', color: '#8b5cf6', estados: ['PENDIENTE_DE_AUTORIZACION'], taskColor: 'yellow' },
  { id: 'EN_PROCESO', title: 'En Proceso', color: '#3b82f6', estados: ['EN_PROCESO', 'EN_PROCESO', 'INPROGRESS'], taskColor: 'blue' },
  { id: 'FINALIZADO', title: 'Finalizado', color: '#10b981', estados: ['FINALIZADO', 'DONE', 'COMPLETADO', 'FINISHED'], taskColor: 'green' },
];

const ESTADO_FILTER_OPTS = COLUMNS.map(c => ({ value: c.id, label: c.title }));

/** Orden de filas en vista Lista (/mis-tareas). */
const LIST_VIEW_ESTADO_ORDER: readonly string[] = [
  'EN_PROCESO',
  'NO_AUTORIZADO',
  'PENDIENTE_DE_AUTORIZACION',
  'PENDIENTE',
  'BLOQUEADO',
  'FINALIZADO',
];

function listViewEstadoSortIndex(estadoId: string): number {
  const idx = LIST_VIEW_ESTADO_ORDER.indexOf(estadoId);
  return idx === -1 ? LIST_VIEW_ESTADO_ORDER.length : idx;
}

const KANBAN_TRANSITIONS: Record<string, string[]> = {
  EN_PROCESO: ['FINALIZADO', 'PENDIENTE', 'PENDIENTE_DE_AUTORIZACION', 'NO_AUTORIZADO'],
  PENDIENTE: ['EN_PROCESO', 'NO_AUTORIZADO', 'FINALIZADO', 'PENDIENTE_DE_AUTORIZACION'],
  PENDIENTE_DE_AUTORIZACION: ['EN_PROCESO', 'NO_AUTORIZADO', 'FINALIZADO'],
  FINALIZADO: ['EN_PROCESO'],
};

function isKanbanColumnDropAllowed(fromColumnId: string, toColumnId: string): boolean {
  if (fromColumnId === toColumnId) return true;
  return (KANBAN_TRANSITIONS[fromColumnId] ?? []).includes(toColumnId);
}

function findTaskColumnIndex(columns: Column[], taskId: string): {
  srcIdx: number;
  srcTaskIdx: number;
} {
  for (let i = 0; i < columns.length; i++) {
    const srcTaskIdx = columns[i].tasks.findIndex(t => t.id === taskId);
    if (srcTaskIdx !== -1) return { srcIdx: i, srcTaskIdx };
  }
  return { srcIdx: -1, srcTaskIdx: -1 };
}

async function parseMoveErrorMessage(
  res: Response,
  fallback: string,
): Promise<string> {
  let msg = fallback;
  try {
    const text = await res.text();
    if (text) {
      try {
        const body = JSON.parse(text);
        msg = body?.details ?? body?.detail ?? body?.message ?? body?.title ?? body?.error ?? text;
      } catch {
        msg = text;
      }
    }
  } catch { /* ignore */ }
  return msg;
}

function isTransitionAllowed(fromColumnId: string, toColumnId: string): boolean {
  if (fromColumnId === toColumnId) return false;
  return (KANBAN_TRANSITIONS[fromColumnId] ?? []).includes(toColumnId);
}

function getAllowedTransitions(fromColumnId: string): { value: string; label: string }[] {
  return (KANBAN_TRANSITIONS[fromColumnId] ?? []).map(id => ({
    value: id,
    label: COLUMNS.find(c => c.id === id)?.title ?? id,
  }));
}

function estadoFilterLabel(estadoId: string): string {
  return ESTADO_FILTER_OPTS.find(o => o.value === estadoId)?.label ?? estadoId;
}

function resolveColumn(estado?: string): ColumnDef {
  if (!estado) return COLUMNS[1];
  const up = estado.toUpperCase();
  return COLUMNS.find(c => c.estados.includes(up)) ?? { ...COLUMNS[1], id: up, title: estado };
}

function strVal(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function includesIgnoreAccents(haystack: string, query: string): boolean {
  const q = normalizeForSearch(query.trim());
  if (!q) return true;
  return normalizeForSearch(haystack).includes(q);
}

function formatKanbanEmpresa(raw: any): string {
  const { rfc, razonSocial } = getKanbanEmpresa(raw);
  if (rfc && razonSocial) return `${rfc} - ${razonSocial}`;
  return rfc || razonSocial || '—';
}

function formatKanbanWorkflow(raw: any): string {
  const nombre = strVal(raw?.data?.workflow?.nombre ?? raw?.workflow?.nombre ?? raw?.workflowNombre);
  return nombre || '—';
}

function resolveRequiereArchivo(raw: any): boolean {
  if (raw?.requiereArchivo === true) return true;
  if (raw?.requiereArchivo === false) return false;
  return !!(raw?.data?.requiereArchivo);
}

function resolveTieneAdjuntos(raw: any): boolean {
  if (raw?.tieneAdjuntos === true) return true;
  if (raw?.tieneAdjuntos === false) return false;
  return !!(raw?.data?.tieneAdjuntos);
}

function formatKanbanPeriodo(raw: any): string {
  const descripcion = strVal(raw?.periodoDescripcion ?? raw?.data?.periodoDescripcion);
  return descripcion || '—';
}

function formatKanbanActividad(raw: any): string {
  const nombre = strVal(
    raw?.actividad?.nombre
    ?? raw?.actividadNombre
    ?? raw?.categoriaActividadDescripcion
    ?? raw?.data?.categoriaActividadDescripcion,
  );
  return nombre || '—';
}

function getFechaVencimientoSat(raw: any): string | null | undefined {
  return raw?.fechaVencimientoSAT ?? raw?.fechaVencimientoSat ?? null;
}

function getFechaVencimientoProfase(raw: any): string | null | undefined {
  return raw?.fechaVencimientoProfase ?? null;
}

function fmtKanbanVencimientoEmpty() {
  return <span className="kanban-list-view__vencimiento-empty">—</span>;
}

function fmtKanbanVencimientoSat(raw: any) {
  const val = getFechaVencimientoSat(raw);
  if (!val) return fmtKanbanVencimientoEmpty();
  return <DateFormat value={val} pattern={EMPRESA_VENCIMIENTO_DATE_PATTERN} />;
}

function fmtKanbanVencimientoProfase(raw: any, col?: Column) {
  const val = getFechaVencimientoProfase(raw);
  if (!val) return fmtKanbanVencimientoEmpty();
  const finalizado = col?.def.id === 'FINALIZADO'
    || String(raw?.estado ?? '').toUpperCase() === 'FINALIZADO';
  return (
    <DateFormat
      value={val}
      pattern={EMPRESA_VENCIMIENTO_DATE_PATTERN}
      highlight={raw?.vencido === true && !finalizado}
      expiredStyle={ACTIVIDAD_VENCIDA_DATE_STYLE}
    />
  );
}

function isValidoParaTerminar(raw: any): boolean {
  const value = raw?.validoParaTerminar;
  return value === true || value === 'true' || value === 'S' || value === 1 || value === '1';
}

function isActividadArchivada(raw: any): boolean {
  const value = raw?.archivado;
  return value === true || value === 'true' || value === 'S' || value === 1 || value === '1';
}

function actividadAdicionalDisabledReason(raw: any): string | null {
  if (!resolveWorkflowInstanciaId(raw)) {
    return 'No se encontró el proceso asociado a esta tarea.';
  }
  if (!puedeCrearActividadAdicional(raw)) {
    const estado = resolveWorkflowInstanciaEstado(raw);
    if (estado) return `El proceso está en estado ${estado}.`;
    return 'No se puede crear una actividad adicional en este proceso.';
  }
  return null;
}

function isEnProcesoListoParaTerminar(colId: string, raw: any): boolean {
  return colId === 'EN_PROCESO' && isValidoParaTerminar(raw);
}

function matchesEmpresaFilter(raw: any, query: string): boolean {
  if (!query.trim()) return true;
  const { rfc, razonSocial } = getKanbanEmpresa(raw);
  return (
    (rfc != null && includesIgnoreAccents(rfc, query)) ||
    (razonSocial != null && includesIgnoreAccents(razonSocial, query))
  );
}

function matchesDescripcionFilter(task: Task, query: string): boolean {
  if (!query.trim()) return true;
  return includesIgnoreAccents(task.asunto, query);
}

function matchesWorkflowFilter(raw: any, query: string): boolean {
  if (!query.trim()) return true;
  return includesIgnoreAccents(formatKanbanWorkflow(raw), query);
}

function matchesPeriodoFilter(raw: any, query: string): boolean {
  if (!query.trim()) return true;
  return includesIgnoreAccents(formatKanbanPeriodo(raw), query);
}

interface Task {
  id: string;
  asunto: string;
  urgente: boolean;
  color: TaskColor;
  requiereAutorizacion: boolean;
  requiereArchivo: boolean;
  tieneAdjuntos: boolean;
  raw: any;
  sourceColumnId?: string;
}

interface Column {
  def: ColumnDef;
  tasks: Task[];
}

function buildActividadAdicionalContext(task: Task) {
  const raw = task.raw;
  const empresa = formatKanbanEmpresa(raw);
  const workflow = formatKanbanWorkflow(raw);
  const periodo = formatKanbanPeriodo(raw);
  return {
    asunto: task.asunto,
    empresa: empresa !== '—' ? empresa : undefined,
    workflow: workflow !== '—' ? workflow : undefined,
    periodo: periodo !== '—' ? periodo : undefined,
  };
}

function AdjuntosIcon({
  show,
  onClick,
}: {
  show?: boolean;
  onClick?: () => void;
}) {
  if (!show) return null;

  if (onClick) {
    return (
      <button
        type="button"
        className="actividades-archivo-download-btn kanban-adjuntos-icon-btn"
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        title="Ver adjuntos"
        aria-label="Ver adjuntos"
      >
        <FaPaperclip size={12} />
      </button>
    );
  }

  return (
    <FaPaperclip
      size={12}
      color="#64748b"
      title="Tiene adjuntos"
      aria-label="Tiene adjuntos"
      style={{ flexShrink: 0, marginLeft: 4 }}
    />
  );
}

function ActividadAdicionalAddButton({
  task,
  disabled,
  onClick,
  className,
  variant = 'icon',
}: {
  task: Task;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  variant?: 'icon' | 'labeled';
}) {
  if (!resolveWorkflowInstanciaId(task.raw)) return null;

  const blockReason = actividadAdicionalDisabledReason(task.raw);
  const isDisabled = disabled || blockReason != null;
  const title = blockReason ?? 'Agregar actividad adicional';

  return (
    <button
      type="button"
      className={[
        className ?? 'kanban-actividad-adicional-btn',
        variant === 'labeled' ? 'kanban-actividad-adicional-btn--labeled' : '',
      ].filter(Boolean).join(' ')}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      disabled={isDisabled}
      title={title}
      aria-label={title}
    >
      <FaPlus size={12} />
      {variant === 'labeled' && <span>Actividad adicional</span>}
    </button>
  );
}

function isTaskEnProceso(task: Task, columnId?: string): boolean {
  const estado = (
    resolveActividadEstado(task.raw) || columnId || task.sourceColumnId || ''
  ).toUpperCase();
  return estado === 'EN_PROCESO';
}

function ActividadAdicionalManageButtons({
  task,
  disabled,
  editable = true,
  onEdit,
  onDelete,
}: {
  task: Task;
  disabled?: boolean;
  editable?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  if (!isActividadAdicional(task.raw) || !editable) return null;

  const blockReason = actividadAdicionalEditDisabledReason(task.raw);
  const canManage = puedeEditarActividadAdicional(task.raw);
  const title = blockReason ?? undefined;

  return (
    <>
      <button
        type="button"
        className="kanban-actividad-adicional-manage-btn"
        disabled={disabled || !canManage}
        title={title ?? 'Editar actividad adicional'}
        aria-label={title ?? 'Editar actividad adicional'}
        onClick={e => {
          e.stopPropagation();
          onEdit();
        }}
      >
        <FaEdit size={12} />
        <span>Editar</span>
      </button>
      <button
        type="button"
        className="kanban-actividad-adicional-manage-btn kanban-actividad-adicional-manage-btn--danger"
        disabled={disabled || !canManage}
        title={title ?? 'Eliminar actividad adicional'}
        aria-label={title ?? 'Eliminar actividad adicional'}
        onClick={e => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <FaTrash size={12} />
        <span>Eliminar</span>
      </button>
    </>
  );
}

function isTaskFinalizado(task: Task, col?: Column): boolean {
  const estado = String(col?.def.id ?? task.raw?.estado ?? '').toUpperCase();
  return estado === 'FINALIZADO';
}

function isTaskVencida(task: Task, col?: Column): boolean {
  if (isTaskFinalizado(task, col)) return false;
  return task.raw?.vencido === true;
}

const KANBAN_VENCIDO_ESTADO_COLOR = '#dc2626';

function kanbanEstadoBadgeColor(col: Column, raw: any): string {
  if (!isTaskFinalizado({ raw } as Task, col) && raw?.vencido === true) {
    return KANBAN_VENCIDO_ESTADO_COLOR;
  }
  return col.def.color;
}

function compareKanbanListRows(
  a: { task: Task; col: Column },
  b: { task: Task; col: Column },
): number {
  const aVencido = isTaskVencida(a.task, a.col);
  const bVencido = isTaskVencida(b.task, b.col);
  if (aVencido !== bVencido) return aVencido ? -1 : 1;

  const byEstado = listViewEstadoSortIndex(a.col.def.id) - listViewEstadoSortIndex(b.col.def.id);
  if (byEstado !== 0) return byEstado;

  const aListo = isEnProcesoListoParaTerminar(a.col.def.id, a.task.raw);
  const bListo = isEnProcesoListoParaTerminar(b.col.def.id, b.task.raw);
  if (aListo !== bListo) return aListo ? -1 : 1;

  return a.task.asunto.localeCompare(b.task.asunto, 'es', { sensitivity: 'base' });
}

interface KanbanFilters {
  selectedEstados: string[];
  vencidoFilter: boolean;
  mailStatusFilter: string;
  empresaFilter: string;
  descripcionFilter: string;
  workflowFilter: string;
  periodoFilter: string;
}

function resolveTaskMailStatus(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const item = raw as Record<string, unknown>;
  const direct = item.mailStatus ?? item.mail_status;
  if (direct != null && String(direct).trim()) {
    return String(direct).toUpperCase();
  }
  const mail = item.mail;
  if (mail && typeof mail === 'object') {
    const status = (mail as Record<string, unknown>).status;
    if (status != null && String(status).trim()) {
      return String(status).toUpperCase();
    }
  }
  return '';
}

function mailStatusFilterLabel(mailStatus: string): string {
  const up = mailStatus.toUpperCase();
  if (up === 'ENVIADO') return 'Pendientes de Cerrar';
  return actividadMailStatusLabel(up);
}

function readKanbanFilters(searchParams: URLSearchParams): KanbanFilters {
  return {
    selectedEstados: searchParams.getAll('estado').filter(Boolean),
    vencidoFilter: searchParams.get('vencido') === 'true',
    mailStatusFilter: searchParams.get('mailStatus') ?? '',
    empresaFilter: searchParams.get('empresa') ?? '',
    descripcionFilter: searchParams.get('descripcion') ?? '',
    workflowFilter: searchParams.get('workflow') ?? '',
    periodoFilter: searchParams.get('periodo') ?? '',
  };
}

function hasKanbanActiveFilters(filters: KanbanFilters): boolean {
  return (
    filters.selectedEstados.length > 0 ||
    filters.vencidoFilter ||
    filters.mailStatusFilter.trim() !== '' ||
    filters.empresaFilter.trim() !== '' ||
    filters.descripcionFilter.trim() !== '' ||
    filters.workflowFilter.trim() !== '' ||
    filters.periodoFilter.trim() !== ''
  );
}

function taskMatchesKanbanFilters(task: Task, col: Column, filters: KanbanFilters): boolean {
  if (filters.selectedEstados.length > 0 && !filters.selectedEstados.includes(col.def.id)) {
    return false;
  }
  if (filters.vencidoFilter && !isTaskVencida(task, col)) {
    return false;
  }
  const mailStatusQ = filters.mailStatusFilter.trim().toUpperCase();
  if (mailStatusQ && resolveTaskMailStatus(task.raw) !== mailStatusQ) {
    return false;
  }
  const empresaQ = filters.empresaFilter.trim();
  if (empresaQ && !matchesEmpresaFilter(task.raw, empresaQ)) return false;
  const descripcionQ = filters.descripcionFilter.trim();
  if (descripcionQ && !matchesDescripcionFilter(task, descripcionQ)) return false;
  const workflowQ = filters.workflowFilter.trim();
  if (workflowQ && !matchesWorkflowFilter(task.raw, workflowQ)) return false;
  const periodoQ = filters.periodoFilter.trim();
  if (periodoQ && !matchesPeriodoFilter(task.raw, periodoQ)) return false;
  return true;
}

function filterKanbanColumns(columns: Column[], filters: KanbanFilters): Column[] {
  if (!hasKanbanActiveFilters(filters)) return columns;
  return columns.map(col => ({
    ...col,
    tasks: col.tasks.filter(task => taskMatchesKanbanFilters(task, col, filters)),
  }));
}

function resolveDragInsertIndex(
  col: Column,
  tasks: Task[],
  destinationIndex: number,
  filters: KanbanFilters,
): number {
  if (!hasKanbanActiveFilters(filters)) {
    return Math.min(destinationIndex, tasks.length);
  }

  const visibleTasks = tasks.filter(task => taskMatchesKanbanFilters(task, col, filters));
  if (destinationIndex >= visibleTasks.length) {
    return tasks.length;
  }

  const insertBeforeId = visibleTasks[destinationIndex]?.id;
  const insertIdx = tasks.findIndex(task => task.id === insertBeforeId);
  return insertIdx === -1 ? tasks.length : insertIdx;
}

function countKanbanTasks(columns: Column[]): number {
  return columns.reduce((total, col) => total + col.tasks.length, 0);
}

const FINALIZADO_COLUMN_ID = 'FINALIZADO';

const RESUMEN_ESTADO_IDS = ['BLOQUEADO', 'PENDIENTE', 'EN_PROCESO', 'FINALIZADO'] as const;

const RESUMEN_ESTADO_LABELS: Record<(typeof RESUMEN_ESTADO_IDS)[number], string> = {
  BLOQUEADO: 'Bloqueado',
  PENDIENTE: 'Pendiente',
  EN_PROCESO: 'En Proceso',
  FINALIZADO: 'Finalizado',
};

function countKanbanPorEstado(columns: Column[], estadoId: string): number {
  const col = columns.find(c => c.def.id === estadoId);
  if (!col) return 0;
  if (estadoId === 'FINALIZADO' || estadoId === 'EN_PROCESO' || estadoId === 'BLOQUEADO') {
    return col.tasks.length;
  }
  return col.tasks.filter(task => !isTaskVencida(task, col)).length;
}

function countKanbanFinalizadas(columns: Column[]): number {
  return countKanbanPorEstado(columns, FINALIZADO_COLUMN_ID);
}

function countKanbanVencidas(columns: Column[]): number {
  return columns.reduce(
    (total, col) => total + col.tasks.filter(task => isTaskVencida(task, col)).length,
    0,
  );
}

function formatKanbanPorcentaje(total: number, finalizadas: number): number | null {
  if (total <= 0) return null;
  return Math.round((finalizadas / total) * 100);
}

function kanbanAvancePctClass(pct: number): string {
  if (pct >= 75) return 'procesos-avance-pct procesos-avance-pct--high';
  if (pct < 49) return 'procesos-avance-pct procesos-avance-pct--low';
  return 'procesos-avance-pct procesos-avance-pct--mid';
}

function kanbanResumenEstadoLabelClass(estadoId: string): string {
  return estadoId === 'FINALIZADO'
    ? 'procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--green'
    : 'procesos-estado-resumen__estado-label';
}

function KanbanResumenEstadoLabel({
  estadoId,
  onClick,
}: {
  estadoId: string;
  onClick?: () => void;
}) {
  const label = RESUMEN_ESTADO_LABELS[estadoId as (typeof RESUMEN_ESTADO_IDS)[number]] ?? estadoId;
  const labelClass = kanbanResumenEstadoLabelClass(estadoId);

  if (!onClick) {
    return <span className={labelClass}>{label}</span>;
  }

  return (
    <button
      type="button"
      className="procesos-estado-resumen__filter-btn"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      title={`Filtrar por: ${label}`}
      aria-label={`Filtrar por ${label}`}
    >
      <span className={labelClass}>{label}</span>
    </button>
  );
}

function KanbanResumenVencidasLabel({ onClick }: { onClick?: () => void }) {
  const labelClass = 'procesos-estado-resumen__estado-label procesos-estado-resumen__estado-label--red';

  if (!onClick) {
    return <span className={labelClass}>Vencidas</span>;
  }

  return (
    <button
      type="button"
      className="procesos-estado-resumen__filter-btn"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      title="Filtrar por: Vencidas"
      aria-label="Filtrar por Vencidas"
    >
      <span className={labelClass}>Vencidas</span>
    </button>
  );
}

function KanbanActividadesResumenBar({
  columns,
  onEstadoClick,
  onVencidasClick,
}: {
  columns: Column[];
  onEstadoClick: (estadoId: string) => void;
  onVencidasClick: () => void;
}) {
  const total = countKanbanTasks(columns);
  const finalizadas = countKanbanFinalizadas(columns);
  const vencidas = countKanbanVencidas(columns);
  const pct = formatKanbanPorcentaje(total, finalizadas);

  return (
    <div className="procesos-estado-resumen" role="status" aria-live="polite">
      {RESUMEN_ESTADO_IDS.map(estadoId => {
        const cantidad = countKanbanPorEstado(columns, estadoId);
        const pctEstado = total > 0 ? Math.round((cantidad / total) * 100) : null;

        return (
          <div key={estadoId} className="procesos-estado-resumen__estado">
            <KanbanResumenEstadoLabel
              estadoId={estadoId}
              onClick={cantidad > 0 ? () => onEstadoClick(estadoId) : undefined}
            />
            <div className="procesos-estado-resumen__metricas">
              <span className="procesos-estado-resumen__valor">{cantidad}</span>
              <span className="procesos-estado-resumen__valor">
                {pctEstado == null ? 'N/D' : `${pctEstado}%`}
              </span>
            </div>
          </div>
        );
      })}
      <div className="procesos-estado-resumen__estado">
        <KanbanResumenVencidasLabel
          onClick={vencidas > 0 ? onVencidasClick : undefined}
        />
        <div className="procesos-estado-resumen__metricas">
          <span className="procesos-estado-resumen__valor procesos-resumen__value--red">{vencidas}</span>
          <span className="procesos-estado-resumen__valor">
            {total > 0 ? `${Math.round((vencidas / total) * 100)}%` : 'N/D'}
          </span>
        </div>
      </div>
      <div className="procesos-estado-resumen__total">
        <span className="procesos-estado-resumen__total-label">Total</span>
        <span className="procesos-estado-resumen__valor procesos-estado-resumen__valor--total">{total}</span>
      </div>
      <div className="procesos-estado-resumen__estado procesos-estado-resumen__estado--avance">
        <span className="procesos-estado-resumen__estado-label">Avance</span>
        <div className="procesos-estado-resumen__metricas">
          {pct == null ? (
            <span className="procesos-actividades-nd">N/D</span>
          ) : (
            <span className={kanbanAvancePctClass(pct)}>{pct}%</span>
          )}
        </div>
      </div>
    </div>
  );
}

function useKanbanFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readKanbanFilters(searchParams), [searchParams]);
  const hasActiveFilters = hasKanbanActiveFilters(filters);

  const setEmpresaFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value.trim()) params.set('empresa', value);
    else params.delete('empresa');
    setSearchParams(params);
  };

  const setDescripcionFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value.trim()) params.set('descripcion', value);
    else params.delete('descripcion');
    setSearchParams(params);
  };

  const setWorkflowFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value.trim()) params.set('workflow', value);
    else params.delete('workflow');
    setSearchParams(params);
  };

  const setPeriodoFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value.trim()) params.set('periodo', value);
    else params.delete('periodo');
    setSearchParams(params);
  };

  const addEstadoFilter = (estadoId: string) => {
    if (!estadoId || filters.selectedEstados.includes(estadoId)) return;
    const params = new URLSearchParams(searchParams);
    params.append('estado', estadoId);
    setSearchParams(params);
  };

  const setEstadoFilter = (estadoId: string) => {
    if (!estadoId) return;
    const params = new URLSearchParams(searchParams);
    params.delete('estado');
    params.set('estado', estadoId);
    setSearchParams(params);
  };

  const setVencidoFilter = (vencido: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (vencido) params.set('vencido', 'true');
    else params.delete('vencido');
    setSearchParams(params);
  };

  const removeVencidoFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('vencido');
    setSearchParams(params);
  };

  const removeEstadoFilter = (estadoId: string) => {
    const params = new URLSearchParams(searchParams);
    const remaining = params.getAll('estado').filter(v => v !== estadoId);
    params.delete('estado');
    remaining.forEach(v => params.append('estado', v));
    setSearchParams(params);
  };

  const removeMailStatusFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('mailStatus');
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('estado');
    params.delete('vencido');
    params.delete('mailStatus');
    params.delete('empresa');
    params.delete('descripcion');
    params.delete('workflow');
    params.delete('periodo');
    setSearchParams(params);
  };

  return {
    filters,
    hasActiveFilters,
    setEmpresaFilter,
    setDescripcionFilter,
    setWorkflowFilter,
    setPeriodoFilter,
    addEstadoFilter,
    setEstadoFilter,
    setVencidoFilter,
    removeVencidoFilter,
    removeEstadoFilter,
    removeMailStatusFilter,
    clearAllFilters,
  };
}

function KanbanFilterBar({
  filteredCount,
  totalCount,
  filters,
  hasActiveFilters,
  onClearAll,
  onClearWorkflow,
  onClearEmpresa,
  onClearDescripcion,
  onClearPeriodo,
  onRemoveEstado,
  onRemoveVencido,
  onRemoveMailStatus,
}: {
  filteredCount: number;
  totalCount: number;
  filters: KanbanFilters;
  hasActiveFilters: boolean;
  onClearAll: () => void;
  onClearWorkflow: () => void;
  onClearEmpresa: () => void;
  onClearDescripcion: () => void;
  onClearPeriodo: () => void;
  onRemoveEstado: (estadoId: string) => void;
  onRemoveVencido: () => void;
  onRemoveMailStatus: () => void;
}) {
  if (!hasActiveFilters) return null;

  return (
    <>
      <div className="kanban-list-view__filter-banner" role="status">
        <span>
          Se están mostrando {filteredCount} actividades de {totalCount}.
        </span>
        <button
          type="button"
          className="kanban-list-view__clear-filters"
          onClick={onClearAll}
        >
          Mostrar Todo
        </button>
      </div>
      <div className="kanban-list-view__chips">
        {filters.periodoFilter.trim() && (
          <span className="kanban-list-view__chip">
            <span>Periodo: {filters.periodoFilter.trim()}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onClearPeriodo}
              aria-label="Quitar filtro de periodo"
            >
              ×
            </button>
          </span>
        )}
        {filters.workflowFilter.trim() && (
          <span className="kanban-list-view__chip">
            <span>Workflow: {filters.workflowFilter.trim()}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onClearWorkflow}
              aria-label="Quitar filtro de workflow"
            >
              ×
            </button>
          </span>
        )}
        {filters.empresaFilter.trim() && (
          <span className="kanban-list-view__chip">
            <span>Empresa: {filters.empresaFilter.trim()}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onClearEmpresa}
              aria-label="Quitar filtro de empresa"
            >
              ×
            </button>
          </span>
        )}
        {filters.descripcionFilter.trim() && (
          <span className="kanban-list-view__chip">
            <span>Descripción: {filters.descripcionFilter.trim()}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onClearDescripcion}
              aria-label="Quitar filtro de descripción"
            >
              ×
            </button>
          </span>
        )}
        {filters.selectedEstados.map(estadoId => (
          <span key={estadoId} className="kanban-list-view__chip">
            <span>Estado: {estadoFilterLabel(estadoId)}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={() => onRemoveEstado(estadoId)}
              aria-label={`Quitar filtro ${estadoFilterLabel(estadoId)}`}
            >
              ×
            </button>
          </span>
        ))}
        {filters.mailStatusFilter.trim() && (
          <span className="kanban-list-view__chip">
            <span>Mail: {mailStatusFilterLabel(filters.mailStatusFilter)}</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onRemoveMailStatus}
              aria-label={`Quitar filtro ${mailStatusFilterLabel(filters.mailStatusFilter)}`}
            >
              ×
            </button>
          </span>
        )}
        {filters.vencidoFilter && (
          <span className="kanban-list-view__chip">
            <span>Vencidas</span>
            <button
              type="button"
              className="kanban-list-view__chip-remove"
              onClick={onRemoveVencido}
              aria-label="Quitar filtro Vencidas"
            >
              ×
            </button>
          </span>
        )}
      </div>
    </>
  );
}

// ── Task Detail Panel ─────────────────────────────────────────────────────────

function TaskDetail({
  task,
  titleId,
  onClose,
  onFormSuccess,
  onFormError,
}: {
  task: Task;
  titleId: string;
  onClose: () => void;
  onFormSuccess: (task: Task, actionLabel: string) => void;
  onFormError?: () => void;
}) {
  const [adjuntosConfirm, setAdjuntosConfirm] = React.useState<ConfirmConfig | null>(null);
  const [modalView, setModalView] = React.useState<'detail' | 'mail'>('detail');
  const raw = task.raw;
  const empresa = getKanbanEmpresa(raw);
  const workflowNombre = formatKanbanWorkflow(raw);
  const readOnly = !isTaskEnProceso(task);

  React.useEffect(() => {
    setModalView('detail');
  }, [task.id]);

  const field = (label: string, value: any, icon?: React.ReactNode) =>
    value != null && value !== '' ? (
      <div style={d.field}>
        {icon && <span style={d.fieldIcon}>{icon}</span>}
        <span style={d.fieldLabel}>{label}</span>
        <span style={d.fieldValue}>{String(value)}</span>
      </div>
    ) : null;

  return (
    <div className="kanban-task-modal">
      <div className="kanban-task-modal__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {modalView === 'mail' ? (
            <button
              type="button"
              className="kanban-task-modal__nav-btn"
              onClick={() => setModalView('detail')}
              title="Volver a la actividad"
              aria-label="Volver a la actividad"
            >
              <FaArrowLeft size={14} />
            </button>
          ) : null}
          {task.urgente && modalView === 'detail' && <FaFire size={16} color="#f97316" title="Urgente" />}
          <h2 id={titleId} className="profase-modal-title kanban-task-modal__title">
            {modalView === 'mail' ? `Mail — ${task.asunto}` : task.asunto}
          </h2>
        </div>
        <div className="kanban-task-modal__header-actions">
          {modalView === 'detail' ? (
            <button
              type="button"
              className="kanban-task-modal__nav-btn kanban-task-modal__nav-btn--mail"
              onClick={() => setModalView('mail')}
              title="Ver mail"
              aria-label="Ver mail"
            >
              <FaEnvelope size={14} />
              <span>Mail</span>
            </button>
          ) : null}
          <button type="button" className="kanban-task-modal__close" onClick={onClose} title="Cerrar" aria-label="Cerrar">
            <FaTimes size={14} />
          </button>
        </div>
      </div>

      {adjuntosConfirm && modalView === 'detail' ? (
        <div className="kanban-task-modal__confirm">
          <Confirm config={adjuntosConfirm} />
        </div>
      ) : null}

      <div className="kanban-task-modal__content">
        {modalView === 'mail' ? (
          <ActividadMailPanel
            actividadId={raw.id}
            readOnly={readOnly}
            onBack={() => setModalView('detail')}
          />
        ) : (
          <>
        {task.urgente && (
          <div style={{ marginBottom: 12 }}>
            <span style={{
              display: 'inline-block', padding: '3px 12px', borderRadius: 20,
              fontSize: 12, fontWeight: 700, background: '#fef2f2', color: '#b91c1c',
            }}>
              🔴 Urgente
            </span>
          </div>
        )}

        <KanbanForm
          taskId={raw.id}
          sourceEstado={resolveActividadEstado(raw) || task.sourceColumnId}
          requiereArchivo={task.requiereArchivo}
          validoParaTerminar={isValidoParaTerminar(raw)}
          empresaId={empresa.id}
          readOnly={readOnly}
          context={{
            id: raw.id,
            asunto: raw.asunto ?? task.asunto,
            rfc: empresa.rfc,
            razonSocial: empresa.razonSocial,
            grupoEmpresarial: empresa.grupoEmpresarial,
            workflow: workflowNombre !== '—' ? workflowNombre : undefined,
          }}
          onCancel={onClose}
          onError={onFormError}
          onConfirmConfigChange={setAdjuntosConfirm}
          onSuccess={(_vals, action) => {
            onFormSuccess(task, action?.label ?? 'acción');
          }}
        />

        <div className="kanban-task-modal__meta">
          {field('Descripción', raw.descripcion ?? raw.mensaje)}
          {field('Fecha', raw.fecha ?? raw.fechaInicio, <FaCalendarAlt size={12} />)}
          {field('Actividad', raw.actividad?.nombre ?? raw.actividadNombre)}
          {field('Resultado', raw.resultado)}
          {raw.tags?.length > 0 && (
            <div style={d.field}>
              <span style={d.fieldIcon}><FaTag size={12} /></span>
              <span style={d.fieldLabel}>Etiquetas</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {raw.tags.map((t: any, i: number) => (
                  <span key={i} style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: t.color === 'yellow' ? '#fef3c7' : t.color === 'green' ? '#d1fae5' : '#fef2f2',
                    color: t.color === 'yellow' ? '#92400e' : t.color === 'green' ? '#065f46' : '#991b1b',
                  }}>{t.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}

const d: Record<string, React.CSSProperties> = {
  field: { display: 'flex', alignItems: 'baseline', gap: 8 },
  fieldIcon: { color: '#94a3b8', flexShrink: 0 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#64748b', minWidth: 100, flexShrink: 0 },
  fieldValue: { fontSize: 13, color: '#1e293b', flex: 1 },
};

// ── Main component ────────────────────────────────────────────────────────────

type KanbanVista = 'kanban' | 'lista';

function KanbanVistaToggle({
  value,
  onChange,
}: {
  value: KanbanVista;
  onChange: (vista: KanbanVista) => void;
}) {
  return (
    <div className="actividades-vista-toggle" role="group" aria-label="Vista">
      <div className="actividades-vista-toggle__options">
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'lista' ? ' is-active' : ''}`}
          aria-pressed={value === 'lista'}
          onClick={() => onChange('lista')}
        >
          Lista
        </button>
        <button
          type="button"
          className={`actividades-vista-toggle__btn${value === 'kanban' ? ' is-active' : ''}`}
          aria-pressed={value === 'kanban'}
          onClick={() => onChange('kanban')}
        >
          Kanban
        </button>
      </div>
    </div>
  );
}

function KanbanSelectedTaskBar({
  task,
  columnId,
  moving,
  onOpen,
  onAddAdicional,
  onEditAdicional,
  onDeleteAdicional,
  onClear,
}: {
  task: Task;
  columnId: string;
  moving: boolean;
  onOpen: () => void;
  onAddAdicional: () => void;
  onEditAdicional: () => void;
  onDeleteAdicional: () => void;
  onClear: () => void;
}) {
  const raw = task.raw;
  const workflow = formatKanbanWorkflow(raw);
  const periodo = formatKanbanPeriodo(raw);
  const actividad = formatKanbanActividad(raw);
  const editable = isTaskEnProceso(task, columnId);
  const showAdicional = resolveWorkflowInstanciaId(raw) != null;

  return (
    <div className="kanban-selected-task-bar" role="region" aria-label="Tarea seleccionada">
      <div className="kanban-selected-task-bar__info">
        <div className="kanban-selected-task-bar__meta">
          {workflow !== '—' && (
            <div className="kanban-selected-task-bar__meta-item">
              <span className="kanban-selected-task-bar__meta-label">Proceso</span>
              <span className="kanban-selected-task-bar__meta-value">{workflow}</span>
            </div>
          )}
          {periodo !== '—' && (
            <div className="kanban-selected-task-bar__meta-item">
              <span className="kanban-selected-task-bar__meta-label">Periodo</span>
              <span className="kanban-selected-task-bar__meta-value">{periodo}</span>
            </div>
          )}
          {actividad !== '—' && (
            <div className="kanban-selected-task-bar__meta-item">
              <span className="kanban-selected-task-bar__meta-label">Actividad</span>
              <span className="kanban-selected-task-bar__meta-value">{actividad}</span>
            </div>
          )}
        </div>
        <div className="kanban-selected-task-bar__asunto">
          {task.urgente && <FaFire size={14} color="#f97316" title="Urgente" />}
          {isActividadAdicional(raw) && (
            <span className="kanban-actividad-adicional-badge">Adicional</span>
          )}
          <span>{task.asunto}</span>
          <AdjuntosIcon show={task.tieneAdjuntos} />
          <ShieldIcon show={task.requiereAutorizacion} />
        </div>
      </div>

      <div className="kanban-selected-task-bar__actions">
        <ActividadAdicionalManageButtons
          task={task}
          disabled={moving}
          editable={editable}
          onEdit={onEditAdicional}
          onDelete={onDeleteAdicional}
        />
        {showAdicional && !isActividadAdicional(raw) && (
          <ActividadAdicionalAddButton
            task={task}
            disabled={moving}
            variant="labeled"
            onClick={onAddAdicional}
          />
        )}
        <button
          type="button"
          className="kanban-selected-task-bar__abrir-btn"
          disabled={moving}
          onClick={onOpen}
        >
          {editable ? 'Editar' : 'Detalles'}
        </button>
        <button
          type="button"
          className="kanban-selected-task-bar__clear-btn"
          onClick={onClear}
          aria-label="Deseleccionar tarea"
          title="Deseleccionar"
        >
          <FaTimes size={14} />
        </button>
      </div>
    </div>
  );
}

function KanbanListView({
  columns,
  loading,
  activeTaskId,
  movingTaskId,
  onOpenTask,
  onMoveTask,
  onAddActividadAdicional,
  onEditActividadAdicional,
  onDeleteActividadAdicional,
  onOpenAdjuntos,
}: {
  columns: Column[];
  loading: boolean;
  activeTaskId?: string | null;
  movingTaskId?: string | null;
  onOpenTask: (task: Task) => void;
  onMoveTask: (taskId: string, fromEstadoId: string, toEstadoId: string) => void;
  onAddActividadAdicional: (task: Task) => void;
  onEditActividadAdicional: (task: Task) => void;
  onDeleteActividadAdicional: (task: Task) => void;
  onOpenAdjuntos: (task: Task) => void;
}) {
  const {
    filters,
    hasActiveFilters,
    setEmpresaFilter,
    setDescripcionFilter,
    setWorkflowFilter,
    setPeriodoFilter,
    addEstadoFilter,
    removeEstadoFilter,
    removeVencidoFilter,
    removeMailStatusFilter,
    clearAllFilters,
  } = useKanbanFilters();

  const allRows = useMemo(
    () => columns.flatMap(col => col.tasks.map(task => ({ task, col }))),
    [columns],
  );

  const filteredColumns = useMemo(
    () => filterKanbanColumns(columns, filters),
    [columns, filters],
  );

  const rows = useMemo(
    () => filteredColumns
      .flatMap(col => col.tasks.map(task => ({ task, col })))
      .sort(compareKanbanListRows),
    [filteredColumns],
  );

  const totalCount = allRows.length;
  const filteredCount = rows.length;

  const hasAnyTasks = columns.some(c => c.tasks.length > 0);
  if (loading && !hasAnyTasks) {
    return <div className="kanban-list-view__loading">Cargando actividades…</div>;
  }

  if (!hasAnyTasks) {
    return (
      <div style={s.empty}>
        <FaInbox style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 12 }} />
        <span style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>No hay Tareas</span>
        <span style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>No tienes actividades asignadas en este momento.</span>
      </div>
    );
  }

  return (
    <div className="kanban-list-view">
      <KanbanFilterBar
        filteredCount={filteredCount}
        totalCount={totalCount}
        filters={filters}
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        onClearWorkflow={() => setWorkflowFilter('')}
        onClearEmpresa={() => setEmpresaFilter('')}
        onClearDescripcion={() => setDescripcionFilter('')}
        onClearPeriodo={() => setPeriodoFilter('')}
        onRemoveEstado={removeEstadoFilter}
        onRemoveVencido={removeVencidoFilter}
        onRemoveMailStatus={removeMailStatusFilter}
      />

      <div className="kanban-list-view__scroll">
      <table className="kanban-list-view__table">
        <thead>
          <tr>
            <th>
              <div className="kanban-list-view__header-filter">
                <span className="kanban-list-view__header-label">Periodo</span>
                <input
                  type="text"
                  className="kanban-list-view__filter-control"
                  value={filters.periodoFilter}
                  onChange={e => setPeriodoFilter(e.target.value)}
                  placeholder="Buscar periodo"
                  aria-label="Filtrar por periodo"
                />
              </div>
            </th>
            <th>
              <div className="kanban-list-view__header-filter">
                <span className="kanban-list-view__header-label">Workflow</span>
                <input
                  type="text"
                  className="kanban-list-view__filter-control"
                  value={filters.workflowFilter}
                  onChange={e => setWorkflowFilter(e.target.value)}
                  placeholder="Buscar workflow"
                  aria-label="Filtrar por workflow"
                />
              </div>
            </th>
            <th>
              <div className="kanban-list-view__header-filter">
                <span className="kanban-list-view__header-label">Empresa</span>
                <input
                  type="text"
                  className="kanban-list-view__filter-control"
                  value={filters.empresaFilter}
                  onChange={e => setEmpresaFilter(e.target.value)}
                  placeholder="RFC o razón social"
                  aria-label="Filtrar por empresa"
                />
              </div>
            </th>
            <th>
              <div className="kanban-list-view__header-filter">
                <span className="kanban-list-view__header-label">Descripción</span>
                <input
                  type="text"
                  className="kanban-list-view__filter-control"
                  value={filters.descripcionFilter}
                  onChange={e => setDescripcionFilter(e.target.value)}
                  placeholder="Buscar descripción"
                  aria-label="Filtrar por descripción"
                />
              </div>
            </th>
            <th className="kanban-list-view__listo-th" aria-label="Listo para terminar" />
            <th>Venc. SAT</th>
            <th>Venc. Profase</th>
            <th>
              <div className="kanban-list-view__header-filter">
                <span className="kanban-list-view__header-label">Estado</span>
                <select
                  className="kanban-list-view__filter-control"
                  value=""
                  onChange={e => addEstadoFilter(e.target.value)}
                  aria-label="Filtrar por estado"
                >
                  <option value="">Filtrar…</option>
                  {ESTADO_FILTER_OPTS.map(opt => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={filters.selectedEstados.includes(opt.value)}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={9} className="kanban-list-view__empty-filter">
                Ninguna actividad coincide con los filtros seleccionados.
              </td>
            </tr>
          ) : (
            rows.map(({ task, col }) => {
            const isActive = activeTaskId === task.id;
            const isMoving = movingTaskId === task.id;
            const transitions = getAllowedTransitions(col.def.id);
            const empresa = formatKanbanEmpresa(task.raw);
            const workflow = formatKanbanWorkflow(task.raw);
            const periodo = formatKanbanPeriodo(task.raw);
            return (
              <tr key={task.id} className={isActive ? 'is-active' : undefined}>
                <td>{periodo}</td>
                <td>{workflow}</td>
                <td>{empresa}</td>
                <td>
                  <div className="kanban-list-view__asunto">
                    {task.urgente && <FaFire size={14} color="#f97316" title="Urgente" />}
                    {isActividadAdicional(task.raw) && (
                      <span className="kanban-actividad-adicional-badge">Adicional</span>
                    )}
                    <span>{task.asunto}</span>
                    <AdjuntosIcon
                      show={task.tieneAdjuntos}
                      onClick={() => onOpenAdjuntos(task)}
                    />
                    <ShieldIcon show={task.requiereAutorizacion} />
                  </div>
                </td>
                <td className="kanban-list-view__listo-cell">
                  {isEnProcesoListoParaTerminar(col.def.id, task.raw) ? (
                    <FaCheck
                      size={14}
                      color="#16a34a"
                      title="Válido para terminar"
                      aria-label="Válido para terminar"
                    />
                  ) : null}
                </td>
                <td className="kanban-list-view__vencimiento-cell">
                  {fmtKanbanVencimientoSat(task.raw)}
                </td>
                <td className="kanban-list-view__vencimiento-cell">
                  {fmtKanbanVencimientoProfase(task.raw, col)}
                </td>
                <td>
                  <div className="kanban-list-view__estado-cell">
                    <button
                      type="button"
                      className="kanban-list-view__estado-btn"
                      style={{ background: kanbanEstadoBadgeColor(col, task.raw) }}
                      onClick={() => addEstadoFilter(col.def.id)}
                      title={`Filtrar por ${col.def.title}`}
                    >
                      {col.def.title}
                    </button>
                    {isActividadArchivada(task.raw) && (
                      <span className="kanban-list-view__archivado-label">⚠️ Archivado</span>
                    )}
                  </div>
                </td>
                <td className="kanban-list-view__actions">
                  <div className="kanban-list-view__actions-inner">
                    <ActividadAdicionalManageButtons
                      task={task}
                      disabled={isMoving || isActive}
                      editable={isTaskEnProceso(task, col.def.id)}
                      onEdit={() => onEditActividadAdicional(task)}
                      onDelete={() => onDeleteActividadAdicional(task)}
                    />
                    {!isActividadAdicional(task.raw) && (
                      <ActividadAdicionalAddButton
                        task={task}
                        disabled={isMoving || isActive}
                        onClick={() => onAddActividadAdicional(task)}
                      />
                    )}
                    {transitions.length > 0 && (
                      <select
                        className="kanban-list-view__transition-control"
                        value=""
                        disabled={isMoving || isActive}
                        onChange={e => {
                          const toEstado = e.target.value;
                          if (toEstado) onMoveTask(task.id, col.def.id, toEstado);
                        }}
                        aria-label="Cambiar estado de la actividad"
                      >
                        <option value="">Mover a…</option>
                        {transitions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      style={s.abrirBtn}
                      onClick={() => onOpenTask(task)}
                      disabled={isActive || isMoving}
                    >
                      {isTaskEnProceso(task, col.def.id) ? 'Editar' : 'Detalles'}
                    </button>
                  </div>
                </td>
              </tr>
            );
            })
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}

type ActividadAdicionalModalState =
  | { mode: 'create'; sourceTask: Task }
  | { mode: 'edit'; task: Task };

const KanbanBoard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columns, setColumns] = useState<Column[]>(COLUMNS.map(def => ({ def, tasks: [] })));
  const [loading, setLoading] = useState(false);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [selectedKanbanTaskId, setSelectedKanbanTaskId] = useState<string | null>(null);
  const [actividadAdicionalModal, setActividadAdicionalModal] = useState<ActividadAdicionalModalState | null>(null);
  const [savingAdicional, setSavingAdicional] = useState(false);
  const [deletingAdicionalId, setDeletingAdicionalId] = useState<string | null>(null);
  const [adjuntosModal, setAdjuntosModal] = useState<{
    actividadId: string | number;
    title: string;
    context: ActividadAdjuntosListModalContext;
  } | null>(null);
  const [movingTaskId, setMovingTaskId] = useState<string | null>(null);
  const [draggingFromColumnId, setDraggingFromColumnId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'error' | 'success' | 'warn' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kanbanJustDraggedRef = useRef(false);
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const showToast = useCallback((msg: string, kind: 'error' | 'success' | 'warn' = 'error', ms = 5000) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind });
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);

  const notifyMoveError = useCallback((
    msg: string,
    fromColumnId?: string,
    toColumnId?: string,
  ) => {
    if (shouldPlayWrongSoundOnFinalizadoTransitionError(fromColumnId, toColumnId)) {
      playSoundDeferred(WRONG_SOUND);
    } else {
      playWrongSound();
    }
    showToast(msg, 'error');
  }, [showToast]);

  const incluirArchivado = searchParams.get('incluirArchivado') === 'S';
  const mailStatusFilter = searchParams.get('mailStatus') ?? '';
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // ── Data ───────────────────────────────────────────────────────────────────
  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (incluirArchivado) params.set('incluirArchivado', 'S');
      if (mailStatusFilter.trim()) params.set('mailStatus', mailStatusFilter.trim());
      const qs = params.toString();
      const url = qs ? `${API_URL}?${qs}` : API_URL;
      const res = await fetch(url, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: any[] = await res.json();

      const colMap: Record<string, Task[]> = {};
      data.forEach((item: any) => {
        const col = resolveColumn(item.estado);
        if (!colMap[col.id]) colMap[col.id] = [];
        colMap[col.id].push({
          id: String(item.id ?? Math.random()),
          asunto: item.asunto ?? item.titulo ?? item.nombre ?? `#${item.id}`,
          urgente: !!item.urgente,
          color: item.urgente ? 'red' : col.taskColor,
          requiereAutorizacion: item.requiereAutorizacion,
          requiereArchivo: resolveRequiereArchivo(item),
          tieneAdjuntos: resolveTieneAdjuntos(item),
          raw: item,
        });
      });

      const knownIds = new Set(COLUMNS.map(c => c.id));
      const extraIds = Object.keys(colMap).filter(id => !knownIds.has(id));
      setColumns([
        ...COLUMNS.map(def => ({ def, tasks: colMap[def.id] ?? [] })),
        ...extraIds.map(id => ({
          def: { id, title: id, color: '#94a3b8', estados: [id], taskColor: 'white' as TaskColor },
          tasks: colMap[id] ?? [],
        })),
      ]);
    } catch (e: any) {
      if (searchParamsRef.current.get('vista') === 'kanban') playWrongSound();
      showToast(e?.message ?? 'Error al cargar actividades', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [incluirArchivado, mailStatusFilter, showToast]);

  const submitTaskMove = useCallback((
    taskId: string,
    fromColumnId: string,
    toColumnId: string,
    rollback: () => void,
  ) => {
    fetch(`/api/usuario/me/actividad/${taskId}/mover`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ estado: toColumnId }),
    }).then(async res => {
      if (!res.ok) {
        rollback();
        const msg = await parseMoveErrorMessage(
          res,
          `No se pudo mover la tarea a "${estadoFilterLabel(toColumnId)}"`,
        );
        notifyMoveError(msg, fromColumnId, toColumnId);
        return;
      }
      showToast(`Tarea movida a "${estadoFilterLabel(toColumnId)}" correctamente`, 'success');
      loadData({ silent: true });
    }).catch(() => {
      rollback();
      notifyMoveError('Error de red al mover la tarea.', fromColumnId, toColumnId);
    }).finally(() => {
      setMovingTaskId(null);
    });
  }, [loadData, notifyMoveError, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTaskFormSuccess = useCallback((_task: Task, actionLabel: string) => {
    setActiveTask(null);
    showToast(`Formulario "${actionLabel}" enviado correctamente`, 'success');
    loadData({ silent: true });
  }, [loadData, showToast]);

  const actividadAdicionalModalContext = useMemo(() => {
    if (!actividadAdicionalModal) return null;
    const task = actividadAdicionalModal.mode === 'create'
      ? actividadAdicionalModal.sourceTask
      : actividadAdicionalModal.task;
    return buildActividadAdicionalContext(task);
  }, [actividadAdicionalModal]);

  const actividadAdicionalEditRaw = actividadAdicionalModal?.mode === 'edit'
    ? actividadAdicionalModal.task.raw
    : undefined;

  const actividadAdicionalEditId = actividadAdicionalModal?.mode === 'edit'
    ? (actividadAdicionalModal.task.raw?.id ?? actividadAdicionalModal.task.id)
    : null;

  const handleActividadAdicionalSubmit = useCallback(async (payload: ActividadAdicionalFormPayload) => {
    if (!actividadAdicionalModal) return;

    setSavingAdicional(true);
    try {
      if (actividadAdicionalModal.mode === 'create') {
        const wfId = resolveWorkflowInstanciaId(actividadAdicionalModal.sourceTask.raw);
        if (!wfId) throw new Error('No se pudo determinar el proceso de la tarea.');
        await crearActividadAdicional(wfId, payload);
        playSound(MAGIC_SOUND);
        showToast('Actividad adicional creada correctamente', 'success');
      } else {
        const actividadId = actividadAdicionalModal.task.raw?.id ?? actividadAdicionalModal.task.id;
        await actualizarActividadAdicional(actividadId, payload);
        showToast('Actividad adicional actualizada correctamente', 'success');
      }
      setActividadAdicionalModal(null);
      await loadData({ silent: true });
    } finally {
      setSavingAdicional(false);
    }
  }, [actividadAdicionalModal, loadData, showToast]);

  const performDeleteActividadAdicional = useCallback(async (task: Task) => {
    setConfirmLoading(true);
    setDeletingAdicionalId(task.id);
    try {
      const actividadId = task.raw?.id ?? task.id;
      await eliminarActividadAdicional(actividadId);
      dismiss();
      if (selectedKanbanTaskId === task.id) setSelectedKanbanTaskId(null);
      showToast('Actividad adicional eliminada correctamente', 'success');
      await loadData({ silent: true });
    } catch (e: unknown) {
      dismiss();
      showToast(
        e instanceof Error ? e.message : 'No se pudo eliminar la actividad adicional.',
        'error',
      );
    } finally {
      setDeletingAdicionalId(null);
      setConfirmLoading(false);
    }
  }, [dismiss, loadData, selectedKanbanTaskId, setConfirmLoading, showToast]);

  const handleDeleteActividadAdicional = useCallback((task: Task) => {
    ask(
      <>¿Eliminar la actividad adicional <strong>{task.asunto}</strong>? Esta acción no se puede deshacer.</>,
      () => { void performDeleteActividadAdicional(task); },
      { title: 'Eliminar actividad adicional', confirmLabel: 'Eliminar', variant: 'danger' },
    );
  }, [ask, performDeleteActividadAdicional]);

  const openCreateActividadAdicional = useCallback((task: Task) => {
    setActividadAdicionalModal({ mode: 'create', sourceTask: task });
  }, []);

  const openEditActividadAdicional = useCallback((task: Task) => {
    setActividadAdicionalModal({ mode: 'edit', task });
  }, []);

  const openAdjuntosModal = useCallback((task: Task) => {
    const actividadId = task.raw?.id ?? task.id;
    if (actividadId == null || actividadId === '') return;
    const context = buildAdjuntosModalContextFromKanbanTask(task);
    const actividad = context.actividad ?? task.asunto;
    const empresa = context.razonSocial ?? context.rfc ?? '';
    const title = empresa ? `Adjuntos — ${actividad} (${empresa})` : `Adjuntos — ${actividad}`;
    setAdjuntosModal({ actividadId, title, context });
  }, []);

  const moveTaskToEstado = useCallback((
    taskId: string,
    _fromEstadoId: string,
    toEstadoId: string,
  ) => {
    if (activeTask?.id === taskId) return;

    const { srcIdx, srcTaskIdx } = findTaskColumnIndex(columns, taskId);
    if (srcIdx === -1) {
      notifyMoveError('No se encontró la tarea.');
      return;
    }

    const fromColumnId = columns[srcIdx].def.id;

    if (!isTransitionAllowed(fromColumnId, toEstadoId)) {
      playWrongSoundForTransitionError(fromColumnId, toEstadoId);
      showToast('Transición no permitida', 'warn');
      return;
    }

    const destIdx = columns.findIndex(c => c.def.id === toEstadoId);
    if (destIdx === -1) {
      notifyMoveError(`Estado destino no válido: "${estadoFilterLabel(toEstadoId)}"`);
      return;
    }

    const previousColumns = columns.map(c => ({ ...c, tasks: [...c.tasks] }));
    const newCols = columns.map(c => ({ ...c, tasks: [...c.tasks] }));
    const [moved] = newCols[srcIdx].tasks.splice(srcTaskIdx, 1);
    const destCol = newCols[destIdx].def;
    newCols[destIdx].tasks.push({
      ...moved,
      color: moved.urgente ? 'red' : destCol.taskColor,
      raw: { ...moved.raw, estado: toEstadoId },
    });
    setColumns(newCols);
    setMovingTaskId(taskId);
    beginCardMoveFeedback(fromColumnId, toEstadoId);
    submitTaskMove(taskId, fromColumnId, toEstadoId, () => setColumns(previousColumns));
  }, [activeTask, columns, notifyMoveError, showToast, submitTaskMove]);

  const {
    filters,
    hasActiveFilters,
    setEmpresaFilter,
    setDescripcionFilter,
    setWorkflowFilter,
    setPeriodoFilter,
    addEstadoFilter,
    setEstadoFilter,
    setVencidoFilter,
    removeVencidoFilter,
    removeEstadoFilter,
    removeMailStatusFilter,
    clearAllFilters,
  } = useKanbanFilters();

  // ── Drag & drop tasks ──────────────────────────────────────────────────────
  const onDragStart = (start: DragStart) => {
    setDraggingFromColumnId(start.source.droppableId);
  };

  const onDragEnd = useCallback((result: DropResult) => {
    setDraggingFromColumnId(null);
    kanbanJustDraggedRef.current = true;
    window.setTimeout(() => {
      kanbanJustDraggedRef.current = false;
    }, 0);

    const { source, destination, draggableId } = result;
    if (!destination) return;
    // Block move if this card is currently open in the detail panel
    if (activeTask && draggableId === activeTask.id) return;
    // No-op if dropped in the same column at same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const { srcIdx, srcTaskIdx } = findTaskColumnIndex(columns, draggableId);
    if (srcIdx === -1) return;

    const fromColumnId = columns[srcIdx].def.id;
    const crossColumnMove = fromColumnId !== destination.droppableId;

    if (
      crossColumnMove &&
      !isKanbanColumnDropAllowed(fromColumnId, destination.droppableId)
    ) {
      playWrongSoundForTransitionError(fromColumnId, destination.droppableId);
      showToast('Transición no permitida', 'warn');
      return;
    }

    const destIdx = columns.findIndex(c => c.def.id === destination.droppableId);
    if (destIdx === -1) return;

    // Snapshot for rollback
    const previousColumns = columns.map(c => ({ ...c, tasks: [...c.tasks] }));

    // Optimistic update — locate task by id so filtered views stay consistent
    const newCols = columns.map(c => ({ ...c, tasks: [...c.tasks] }));
    const [moved] = newCols[srcIdx].tasks.splice(srcTaskIdx, 1);
    const destInsertIdx = resolveDragInsertIndex(
      newCols[destIdx],
      newCols[destIdx].tasks,
      destination.index,
      filters,
    );
    const destCol = newCols[destIdx].def;
    const updatedMoved = crossColumnMove
      ? {
          ...moved,
          color: moved.urgente ? 'red' : destCol.taskColor,
          raw: { ...moved.raw, estado: destination.droppableId },
        }
      : moved;
    newCols[destIdx].tasks.splice(destInsertIdx, 0, updatedMoved);
    setColumns(newCols);

    if (crossColumnMove) {
      beginCardMoveFeedback(fromColumnId, destination.droppableId);
      setMovingTaskId(draggableId);
      submitTaskMove(
        draggableId,
        fromColumnId,
        destination.droppableId,
        () => setColumns(previousColumns),
      );
    }
  }, [activeTask, columns, filters, showToast, submitTaskMove]);

  const vista: KanbanVista = searchParams.get('vista') === 'kanban' ? 'kanban' : 'lista';
  const isEmpty = columns.every(c => c.tasks.length === 0);
  const showInitialLoading = loading && isEmpty;
  const filteredColumns = useMemo(
    () => filterKanbanColumns(columns, filters),
    [columns, filters],
  );
  const totalCount = useMemo(() => countKanbanTasks(columns), [columns]);
  const filteredCount = useMemo(() => countKanbanTasks(filteredColumns), [filteredColumns]);
  const isFilteredEmpty = hasActiveFilters && filteredCount === 0 && totalCount > 0;

  const selectedKanbanTask = useMemo((): Task | null => {
    if (!selectedKanbanTaskId) return null;
    for (const col of columns) {
      const task = col.tasks.find(t => t.id === selectedKanbanTaskId);
      if (task) return { ...task, sourceColumnId: col.def.id };
    }
    return null;
  }, [columns, selectedKanbanTaskId]);

  const handleKanbanCardSelect = useCallback((taskId: string) => {
    if (kanbanJustDraggedRef.current) return;
    setSelectedKanbanTaskId(taskId);
  }, []);

  const handleVistaChange = (next: KanbanVista) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'lista') {
      params.delete('vista');
      setSelectedKanbanTaskId(null);
    } else {
      params.set('vista', 'kanban');
    }
    setSearchParams(params);
  };

  const handleIncluirArchivadoChange = (checked: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (checked) params.set('incluirArchivado', 'S');
    else params.delete('incluirArchivado');
    setSearchParams(params);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={g.page}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Mis Tareas', icon: <FaColumns /> },
        ]}
      />

      <PageHeader
        icon={<FaColumns />}
        iconColor="#6366f1"
        title="Mis Tareas"
        subtitle="Mis actividades asignadas"
        action={{
          label: (
            <FaSync
              style={{
                fontSize: 14,
                animation: loading ? 'spin 0.7s linear infinite' : undefined,
              }}
            />
          ),
          onClick: loadData,
          disabled: loading,
        }}
      />

      <div className="kanban-toolbar">
        <KanbanVistaToggle value={vista} onChange={handleVistaChange} />
        <label className="kanban-incluir-archivados">
          <input
            type="checkbox"
            checked={incluirArchivado}
            onChange={e => handleIncluirArchivadoChange(e.target.checked)}
          />
          Incluir Archivados
        </label>
      </div>

      {confirmConfig && (
        <div className="kanban-confirm-banner">
          <Confirm config={confirmConfig} />
        </div>
      )}

      {!showInitialLoading && totalCount > 0 && (
        <KanbanActividadesResumenBar
          columns={columns}
          onEstadoClick={setEstadoFilter}
          onVencidasClick={() => setVencidoFilter(true)}
        />
      )}

      <div style={s.root}>

      {/* ── Kanban board / list ── */}
      <div style={s.topPanel}>

        {toast && (
          <div style={{ ...s.toast, ...s[`toast_${toast.kind}`] }}>
            {toast.kind === 'error' && '✖ '}
            {toast.kind === 'success' && '✔ '}
            {toast.kind === 'warn' && '⚠ '}
            {toast.msg}
            <button style={s.toastClose} onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {vista === 'lista' ? (
          <KanbanListView
            columns={columns}
            loading={loading}
            activeTaskId={activeTask?.id}
            movingTaskId={movingTaskId}
            onOpenTask={setActiveTask}
            onMoveTask={moveTaskToEstado}
            onAddActividadAdicional={openCreateActividadAdicional}
            onEditActividadAdicional={openEditActividadAdicional}
            onDeleteActividadAdicional={handleDeleteActividadAdicional}
            onOpenAdjuntos={openAdjuntosModal}
          />
        ) : (
          <div className="kanban-board-view">
            {showInitialLoading && (
              <div className="kanban-list-view__loading">Cargando actividades…</div>
            )}

            {!showInitialLoading && !isEmpty && (
              <KanbanFilterBar
                filteredCount={filteredCount}
                totalCount={totalCount}
                filters={filters}
                hasActiveFilters={hasActiveFilters}
                onClearAll={clearAllFilters}
                onClearWorkflow={() => setWorkflowFilter('')}
                onClearEmpresa={() => setEmpresaFilter('')}
                onClearDescripcion={() => setDescripcionFilter('')}
                onClearPeriodo={() => setPeriodoFilter('')}
                onRemoveEstado={removeEstadoFilter}
                onRemoveVencido={removeVencidoFilter}
                onRemoveMailStatus={removeMailStatusFilter}
              />
            )}

            {!showInitialLoading && isEmpty && (
              <div style={s.empty}>
                <FaInbox style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 12 }} />
                <span style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>No hay Tareas</span>
                <span style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>No tienes actividades asignadas en este momento.</span>
              </div>
            )}

            {!showInitialLoading && isFilteredEmpty && (
              <div className="kanban-list-view__empty-filter kanban-board-view__empty-filter">
                Ninguna actividad coincide con los filtros seleccionados.
              </div>
            )}

            {!showInitialLoading && !isEmpty && !isFilteredEmpty && (
            <>
            {selectedKanbanTask && (
              <KanbanSelectedTaskBar
                task={selectedKanbanTask}
                columnId={selectedKanbanTask.sourceColumnId ?? ''}
                moving={movingTaskId === selectedKanbanTask.id || deletingAdicionalId === selectedKanbanTask.id}
                onOpen={() => setActiveTask(selectedKanbanTask)}
                onAddAdicional={() => openCreateActividadAdicional(selectedKanbanTask)}
                onEditAdicional={() => openEditActividadAdicional(selectedKanbanTask)}
                onDeleteAdicional={() => handleDeleteActividadAdicional(selectedKanbanTask)}
                onClear={() => setSelectedKanbanTaskId(null)}
              />
            )}
            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
              <div style={s.board}>
                {filteredColumns.map(col => {
                  const columnDropDisabled =
                    draggingFromColumnId != null &&
                    !isKanbanColumnDropAllowed(draggingFromColumnId, col.def.id);

                  return (
                  <Droppable droppableId={col.def.id} key={col.def.id}>
                    {(provided, snapshot) => (
                      <div
                        style={{
                          ...s.column,
                          opacity: columnDropDisabled ? 0.5 : 1,
                          transition: 'opacity 0.15s ease',
                          background: snapshot.isDraggingOver ? '#f0f9ff' : '#f8fafc',
                        }}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <div style={{ ...s.colHeader, background: col.def.color, borderBottomColor: col.def.color }}>
                          <span style={{ ...s.colTitle, color: '#fff' }}>{col.def.title}</span>
                          <span style={{ ...s.colCount, background: '#fff', color: col.def.color }}>{col.tasks.length}</span>
                        </div>

                        <div style={s.taskList}>
                          {col.tasks.map((task, index) => (
                            <Draggable
                              draggableId={task.id}
                              index={index}
                              key={task.id}
                              isDragDisabled={activeTask?.id === task.id}
                            >
                              {(provided, snapshot) => {
                                const isSelected = selectedKanbanTaskId === task.id;
                                return (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`postit postit-${task.color}${isTaskVencida(task, col) ? ' postit--vencido' : ''}${isSelected ? ' postit--selected' : ''}`}
                                  style={mergePostitDragStyle(provided.draggableProps.style, snapshot.isDragging)}
                                  onClick={() => handleKanbanCardSelect(task.id)}
                                >
                                  {isTaskVencida(task, col) && (
                                    <span className="kanban-card__vencido-tag">VENCIDO</span>
                                  )}
                                  <div style={s.taskTop}>
                                    {task.urgente && <FaFire size={14} color="#f97316" title="Urgente" style={{ flexShrink: 0 }} />}
                                    {isActividadAdicional(task.raw) && (
                                      <span className="kanban-actividad-adicional-badge">Adicional</span>
                                    )}
                                    {isValidoParaTerminar(task.raw) && (
                                      <span title="Válido para terminar" aria-label="Válido para terminar" style={{ flexShrink: 0 }}>✅</span>
                                    )}
                                    <span style={s.taskLabel}>{task.asunto}</span>
                                    <AdjuntosIcon show={task.tieneAdjuntos} />
                                    <ShieldIcon show={task.requiereAutorizacion}></ShieldIcon>
                                  </div>

                                  {formatKanbanEmpresa(task.raw) !== '—' && (
                                    <span style={s.taskMeta}>
                                      {formatKanbanEmpresa(task.raw)}
                                    </span>
                                  )}
                                  {task.raw.autorizacion ? '[Autorización]' : '-'}
                                  {isActividadArchivada(task.raw) && (
                                    <span className="kanban-list-view__archivado-label kanban-card__archivado-label">
                                      ⚠️ Archivado
                                    </span>
                                  )}
                                </div>
                                );
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {col.tasks.length === 0 && <div style={s.emptyCol}>Sin tareas</div>}
                        </div>
                      </div>
                    )}
                  </Droppable>
                  );
                })}
              </div>
            </DragDropContext>
            </>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInSlide {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      </div>

      <ProfaseModal
        open={activeTask !== null}
        size="full"
        titleId="kanban-task-modal-title"
        onBackdropMouseDown={e => {
          if (e.target === e.currentTarget) setActiveTask(null);
        }}
      >
        {activeTask && (
          <TaskDetail
            task={activeTask}
            titleId="kanban-task-modal-title"
            onClose={() => setActiveTask(null)}
            onFormSuccess={handleTaskFormSuccess}
            onFormError={() => {
              const fromEstado = resolveActividadEstado(activeTask?.raw) || activeTask?.sourceColumnId || '';
              if (vista === 'kanban' && fromEstado !== 'EN_PROCESO' && fromEstado !== 'PENDIENTE') {
                playWrongSound();
              }
            }}
          />
        )}
      </ProfaseModal>

      {adjuntosModal && (
        <ActividadAdjuntosListModal
          open
          title={adjuntosModal.title}
          context={adjuntosModal.context}
          actividadId={adjuntosModal.actividadId}
          onClose={() => setAdjuntosModal(null)}
        />
      )}

      <ActividadAdicionalModal
        open={actividadAdicionalModal !== null}
        mode={actividadAdicionalModal?.mode ?? 'create'}
        taskContext={actividadAdicionalModalContext}
        editTaskRaw={actividadAdicionalEditRaw}
        editActividadId={actividadAdicionalEditId}
        saving={savingAdicional}
        onClose={() => {
          if (!savingAdicional) setActividadAdicionalModal(null);
        }}
        onSubmit={handleActividadAdicionalSubmit}
      />
    </div>
  );
};

export default KanbanBoard;

// ── Styles ────────────────────────────────────────────────────────────────────

const g: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '0 4px',
  },
};

const s: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    userSelect: 'none',
  },
  topPanel: {
    display: 'flex', flexDirection: 'column', gap: 10,
    padding: '12px 16px 0',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px 8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    flexShrink: 0, animation: 'fadeInSlide 0.2s ease',
  },
  toast_error: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  toast_success: { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' },
  toast_warn: { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
  toastClose: {
    all: 'unset', cursor: 'pointer', marginLeft: 'auto', fontSize: 11,
    opacity: 0.6, lineHeight: 1,
  } as React.CSSProperties,
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px',
  },
  board: {
    display: 'flex', flexDirection: 'row', gap: 2,
    overflowX: 'auto', overflowY: 'visible',
    paddingBottom: 8,
    flex: 1,
    alignItems: 'stretch',
  },
  column: {
    flex: '0 0 230px', minWidth: 230,
    display: 'flex', flexDirection: 'column',
    border: '1px solid #e2e8f0', overflow: 'hidden',
    transition: 'background 0.15s',
    alignSelf: 'stretch',
  },
  colHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px', borderBottom: '1px solid transparent', flexShrink: 0,
    boxSizing: 'border-box',
    minHeight: 'calc(16px + 2 * 12px * 1.35)',
  },
  colTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    lineHeight: 1.35,
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  colCount: {
    fontSize: 11, fontWeight: 700, color: '#fff',
    padding: '1px 7px', borderRadius: 10, minWidth: 18, textAlign: 'center' as const,
  },
  taskList: {
    display: 'flex', flexDirection: 'column', gap: 0,
    padding: '6px 2px', minHeight: 40,
    flex: 1,
  },
  taskTop: { display: 'flex', alignItems: 'flex-start', gap: 6 },
  taskLabel: { fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.4, flex: 1, wordBreak: 'break-word' as const },
  taskMeta: { fontSize: 10, color: '#64748b', marginTop: 4, display: 'block' },
  emptyCol: { fontSize: 12, color: '#cbd5e1', textAlign: 'center' as const, padding: '12px 6px', fontStyle: 'italic' },
  abrirBtn: {
    marginTop: 8, padding: '4px 14px',
    background: '#3b82f6', color: '#fff',
    border: 'none', borderRadius: 6,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    display: 'inline-block', alignSelf: 'flex-start' as const,
  },
};
